export type LineItemOrderType = 'hotel' | 'transfer' | 'tour' | 'package' | 'generic';

export interface MetaField {
  label: string;
  value: string;
  isUrl?: boolean;
}

export interface MetaSection {
  title: string;
  fields: MetaField[];
}

export interface TravelerRow {
  room?: string;
  type: string;
  name: string;
  gender?: string;
  dob?: string;
  nationality?: string;
  age?: string;
  weight?: string;
  passport?: string;
  phone?: string;
  email?: string;
}

export interface ParsedLineItemMeta {
  type: LineItemOrderType;
  sections: MetaSection[];
  travelers: TravelerRow[];
  thumbnailUrl?: string;
}

const HIDDEN_KEYS = new Set([
  '_ngk_rate_payload_json',
  '_ngk_rate_cache_key',
  '_ngk_contract_token_id',
  '_ngk_room_type_code',
  '_ngk_meal_plan_code',
  '_ngk_api_booking_pending',
  '_ngk_hotel_booking',
  '_ngk_supplier_original_price',
  '_ngk_original_price',
  '_ngk_hotel_id',
  'traveler_info',
  '_ngk_guests_json',
  '_tat_extra_fields',
  'packageInfo',
  'peak_days',
  'traveler_rooms',
  'parks',
  'perks',
]);

const FIELD_LABELS: Record<string, string> = {
  _ngk_hotel_name: 'Hotel',
  _ngk_check_in: 'Check-in',
  _ngk_check_out: 'Check-out',
  _ngk_room_type: 'Room type',
  _ngk_meal_plan: 'Meal plan',
  _ngk_display_price: 'Price',
  _ngk_supplier_display_price: 'Supplier price',
  _ngk_currency: 'Currency',
  _ngk_supplier_currency: 'Supplier currency',
  _ngk_guest_email: 'Guest email',
  _ngk_guest_phone: 'Guest phone',
  _ngk_nationality: 'Nationality',
  _ngk_special_requests: 'Special requests',
  _ngk_booking_ref: 'Booking reference',
  _ngk_iot_booking_ref: 'IOL booking reference',
  _ngk_hotel_thumbnail: 'Hotel image',
  _tat_vehicle: 'Vehicle',
  _tat_package_name: 'Package',
  _tat_pickup_name: 'Pickup',
  _tat_dropoff_name: 'Drop-off',
  _tat_pickup: 'Pickup code',
  _tat_dropoff: 'Drop-off code',
  _tat_adults: 'Adults',
  _tat_children: 'Children',
  _tat_price: 'Transfer price',
  _tat_terminal_name: 'Terminal service',
  _tat_terminal_service_price: 'Terminal service price',
  _tat_terminal_total: 'Terminal total',
  'Tour ID': 'Tour ID',
  'Tour Title': 'Tour',
  'Tour Price': 'Tour price',
  'Booking Date': 'Booking date',
  Adults: 'Adults',
  Children: 'Children',
  Guests: 'Guests',
  'Room Details': 'Rooms',
  'Supplement/Charges': 'Supplement / charges',
  '1 Adult Charges': '1 adult charges',
  '2-3 Adult Charges': '2–3 adult charges',
  'Child Charges': 'Child charges',
  'Extra Nights': 'Extra nights',
};

const TAT_FIELD_LABELS: Record<string, string> = {
  tat_fields_flight_no: 'Flight number',
  tat_fields_from: 'From',
  tat_fields_to: 'To',
  tat_fields_departure_date: 'Departure date',
  tat_fields_departure_time: 'Departure time',
  tat_fields_arrival_date: 'Arrival date',
  tat_fields_arrival_time: 'Arrival time',
  'tat_fields_hotel_/_accommodation': 'Hotel / accommodation',
  tat_fields_hotel_v_accomodation: 'Hotel / accommodation',
};

/** Normalize WooCommerce meta keys (tat_* vs _tat_*, etc.) */
export function normalizeMetaKeys(meta: Record<string, string>): Record<string, string> {
  const out: Record<string, string> = { ...meta };
  Object.entries(meta).forEach(([key, value]) => {
    if (key.startsWith('tat_') && !key.startsWith('_tat_')) {
      const normalized = `_${key}`;
      if (out[normalized] == null || out[normalized] === '') {
        out[normalized] = value;
      }
    }
  });
  return out;
}

export function metaArrayToRecord(
  meta?: Array<{ key: string; value: string }>
): Record<string, string> {
  const out: Record<string, string> = {};
  (meta || []).forEach((m) => {
    if (m.key) out[m.key] = m.value ?? '';
  });
  return normalizeMetaKeys(out);
}

function decodeHtmlEntitiesOnce(value: string): string {
  let decoded = value;
  if (typeof document !== 'undefined') {
    const textarea = document.createElement('textarea');
    textarea.innerHTML = decoded;
    decoded = textarea.value;
  } else if (typeof DOMParser !== 'undefined') {
    decoded =
      new DOMParser().parseFromString(decoded, 'text/html').documentElement.textContent ?? decoded;
  }
  return decoded
    .replace(/&amp;/gi, '&')
    .replace(/&#0*38;/g, '&')
    .replace(/&#x0*26;/gi, '&')
    .replace(/&nbsp;?/gi, ' ')
    .replace(/&#160;?/g, ' ')
    .replace(/&#x0*A0;?/gi, ' ')
    .replace(/&lt;/gi, '<')
    .replace(/&gt;/gi, '>')
    .replace(/&quot;/gi, '"')
    .replace(/&#(\d+);?/g, (match, code) => {
      const n = Number(code);
      return Number.isFinite(n) ? String.fromCodePoint(n) : match;
    })
    .replace(/&#x([0-9a-f]+);?/gi, (match, hex) => {
      const n = parseInt(hex, 16);
      return Number.isFinite(n) ? String.fromCodePoint(n) : match;
    });
}

export function decodeHtmlEntities(value: string): string {
  let current = String(value ?? '');
  for (let i = 0; i < 4; i += 1) {
    const next = decodeHtmlEntitiesOnce(current);
    if (next === current) break;
    current = next;
  }
  return current.replace(/\s+/g, ' ').trim();
}

function stripPriceMarkup(value: string): string {
  return value.replace(/<\/?(span|bdi|b|small)[^>]*>/gi, ' ');
}

export function formatOrderTotal(
  total: string | number | undefined | null,
  currency?: string
): string {
  if (total == null || total === '') return '—';
  const rawValue =
    typeof total === 'object' ? (total as { value?: string }).value : total;
  const raw = decodeHtmlEntities(stripPriceMarkup(String(rawValue ?? '')));
  if (!raw) return '—';
  const num = Number(raw.replace(/[^0-9.-]/g, ''));
  if (Number.isNaN(num)) return raw;
  const formattedNumber = num.toLocaleString('en-US', { minimumFractionDigits: 2 });
  const symbolFromTotal = raw.replace(/[0-9.,\s-]/g, '').trim();
  const symbol = symbolFromTotal || (currency ? decodeHtmlEntities(String(currency)) : '');
  return symbol ? `${symbol} ${formattedNumber}` : formattedNumber;
}

function isEmpty(value: string | undefined): boolean {
  if (value == null) return true;
  const v = decodeHtmlEntities(String(value));
  return v === '' || v === '—' || v === 'null' || v === '[]' || v === '{}';
}

function humanizeLabel(key: string): string {
  if (FIELD_LABELS[key]) return FIELD_LABELS[key];
  if (TAT_FIELD_LABELS[key]) return TAT_FIELD_LABELS[key];
  return decodeHtmlEntities(
    key
      .replace(/^_ngk_/, '')
      .replace(/^_tat_/, '')
      .replace(/^tat_fields_/, '')
      .replace(/_/g, ' ')
      .replace(/\b\w/g, (c) => c.toUpperCase())
  );
}

function formatMetaValue(key: string, value: string): string {
  const v = decodeHtmlEntities(value);
  if (isEmpty(v)) return '';
  if (/^\d{4}-\d{2}-\d{2}$/.test(v)) {
    try {
      return new Date(v + 'T12:00:00').toLocaleDateString('en-US', {
        year: 'numeric',
        month: 'short',
        day: 'numeric',
      });
    } catch {
      return v;
    }
  }
  if (key.toLowerCase().includes('price') || key === 'Tour Price' || key === '_tat_price') {
    const num = Number(v.replace(/[^0-9.-]/g, ''));
    if (!Number.isNaN(num)) {
      return num.toLocaleString('en-US', { minimumFractionDigits: 2 });
    }
  }
  return v;
}

function field(key: string, value: string | undefined, isUrl?: boolean): MetaField | null {
  if (isEmpty(value)) return null;
  const formatted = formatMetaValue(key, value!);
  if (!formatted) return null;
  const url = isUrl ?? (key.includes('thumbnail') && formatted.startsWith('http'));
  return { label: humanizeLabel(key), value: formatted, isUrl: url };
}

function section(title: string, fields: Array<MetaField | null>): MetaSection | null {
  const valid = fields.filter((f): f is MetaField => f != null);
  if (!valid.length) return null;
  return { title, fields: valid };
}

function tryParseJson<T>(raw: string | undefined): T | null {
  if (isEmpty(raw)) return null;
  try {
    return JSON.parse(raw!) as T;
  } catch {
    return null;
  }
}

/**
 * Prefer the actual booked hotel, tour, or transfer name stored in line-item
 * metadata over generic WooCommerce product titles such as "NGK Hotel Booking".
 */
export function getOrderLineItemDisplayName(
  productName: string | undefined,
  meta?: Array<{ key: string; value: string }>
): string {
  const record = metaArrayToRecord(meta);
  const packageInfo = tryParseJson<Record<string, unknown>>(record.packageInfo);
  const packageHotelName =
    typeof packageInfo?.hotel_name === 'string' ? packageInfo.hotel_name : '';
  const transferRoute = [
    record._tat_pickup_name || record._tat_pickup,
    record._tat_dropoff_name || record._tat_dropoff,
  ]
    .filter(Boolean)
    .join(' → ');

  const candidates = [
    record._ngk_hotel_name,
    record._tat_package_name,
    record['Tour Title'],
    packageHotelName,
    transferRoute,
    record._tat_vehicle,
    record._tat_terminal_name,
    productName,
  ];

  const displayName = candidates.find((value) => !isEmpty(value));
  return displayName ? decodeHtmlEntities(String(displayName)) : 'Product';
}

function parseTravelerInfo(raw: string | undefined): TravelerRow[] {
  const parsed = tryParseJson<unknown>(raw);
  if (!parsed) return [];

  const rooms = Array.isArray(parsed) ? parsed : [parsed];
  const rows: TravelerRow[] = [];

  rooms.forEach((room: unknown) => {
    if (!room || typeof room !== 'object') return;
    const r = room as Record<string, unknown>;
    const roomName = typeof r.roomName === 'string' ? r.roomName : undefined;

    const adults = Array.isArray(r.adults) ? r.adults : [];
    adults.forEach((a: unknown) => {
      const basics = (a as { basics?: Record<string, string> })?.basics || {};
      const first = basics.first_name || '';
      const last = basics.last_name || '';
      const name = [basics.gender, first, last].filter(Boolean).join(' ').trim();
      if (!name && !first && !last) return;
      rows.push({
        room: roomName,
        type: 'Adult',
        name: name || '—',
        gender: basics.gender,
        dob: basics.dob || basics.date_of_birth,
        nationality: basics.nationality,
        weight: basics.weight || basics.weight_kg,
        passport: basics.passport || basics.passport_no || basics.id_passport || basics.id_number,
        phone: basics.phone || basics.mobile || basics.phone_number,
        email: basics.email,
      });
    });

    const children = Array.isArray(r.children) ? r.children : [];
    children.forEach((c: unknown) => {
      const basics = (c as { basics?: Record<string, string> })?.basics || {};
      const first = basics.first_name || '';
      const last = basics.last_name || '';
      const name = [basics.gender, first, last].filter(Boolean).join(' ').trim();
      if (!name && !first && !last) return;
      rows.push({
        room: roomName,
        type: 'Child',
        name: name || '—',
        gender: basics.gender,
        dob: basics.dob || basics.date_of_birth,
        nationality: basics.nationality,
        age: (c as { age?: string })?.age,
        weight: basics.weight || basics.weight_kg,
        passport: basics.passport || basics.passport_no || basics.id_passport || basics.id_number,
        phone: basics.phone || basics.mobile || basics.phone_number,
        email: basics.email,
      });
    });
  });

  return rows;
}

function parseNgkGuests(raw: string | undefined): TravelerRow[] {
  const guests = tryParseJson<Array<Record<string, string>>>(raw);
  if (!guests?.length) return [];
  return guests.map((g, i) => ({
    type: g.type || 'Guest',
    name: [g.title, g.first_name, g.last_name].filter(Boolean).join(' ').trim() || `Guest ${i + 1}`,
    gender: g.gender,
    dob: g.date_of_birth || g.dob,
    nationality: g.nationality,
    age: g.age,
    room: g.room_no ? `Room ${g.room_no}` : undefined,
    weight: g.weight || g.weight_kg,
    passport: g.passport || g.passport_no || g.id_passport || g.id_number,
    phone: g.phone || g.mobile || g.phone_number,
    email: g.email,
  }));
}

function parseTatExtraFields(meta: Record<string, string>): MetaField[] {
  const fromJson = tryParseJson<Array<{ label?: string; value?: string }>>(meta._tat_extra_fields);
  if (fromJson?.length) {
    return fromJson
      .map((item) => {
        const key = item.label || '';
        const val = item.value || '';
        if (isEmpty(val)) return null;
        return {
          label: TAT_FIELD_LABELS[key] || humanizeLabel(key),
          value: formatMetaValue(key, val),
        };
      })
      .filter((f): f is MetaField => f != null);
  }

  return Object.entries(meta)
    .filter(([k]) => k.startsWith('tat_fields_'))
    .map(([k, v]) => field(k, v))
    .filter((f): f is MetaField => f != null);
}

function parsePackageInfo(raw: string | undefined): MetaField[] {
  const pkg = tryParseJson<Record<string, unknown>>(raw);
  if (!pkg) return [];

  const fields: MetaField[] = [];
  if (typeof pkg.hotel_name === 'string') fields.push({ label: 'Hotel', value: pkg.hotel_name });
  if (typeof pkg.date === 'string') fields.push({ label: 'Date', value: formatMetaValue('date', pkg.date) });
  if (pkg.totalAdults != null) fields.push({ label: 'Total adults', value: String(pkg.totalAdults) });
  if (pkg.totalChildren != null) fields.push({ label: 'Total children', value: String(pkg.totalChildren) });

  const rooms = pkg.rooms as Record<string, { adults?: number; children?: number }> | undefined;
  if (rooms && typeof rooms === 'object') {
    Object.entries(rooms).forEach(([roomName, counts]) => {
      if (!counts) return;
      fields.push({
        label: roomName,
        value: `${counts.adults ?? 0} adults, ${counts.children ?? 0} children`,
      });
    });
  }

  return fields;
}

function parsePeakDays(raw: string | undefined): MetaField[] {
  const peaks = tryParseJson<Array<{ date?: string; rate?: number }>>(raw);
  if (!peaks?.length) return [];
  return peaks.slice(0, 5).map((p, i) => ({
    label: `Peak day ${i + 1}`,
    value: [p.date, p.rate != null ? `Rate: ${p.rate}` : ''].filter(Boolean).join(' — '),
  }));
}

export function detectOrderType(
  meta: Record<string, string>,
  productName: string
): LineItemOrderType {
  const name = productName.toLowerCase();
  if (meta._ngk_hotel_booking === 'yes' || meta._ngk_hotel_name) return 'hotel';
  if (meta._tat_vehicle || meta._tat_pickup || meta._tat_pickup_name || name.includes('transfer')) {
    return 'transfer';
  }
  // Package before tour so packageInfo / "package" products are not mislabeled as Tour
  if (meta.packageInfo || name.includes('package')) return 'package';
  if (meta['Tour Title'] || meta['Tour ID'] || name.includes('tour')) return 'tour';
  return 'generic';
}

export function parseOrderLineItemMeta(
  meta: Record<string, string>,
  productName: string
): ParsedLineItemMeta {
  const type = detectOrderType(meta, productName);
  const sections: MetaSection[] = [];
  const consumed = new Set<string>();

  const addSection = (s: MetaSection | null, keys: string[]) => {
    if (!s) return;
    sections.push(s);
    keys.forEach((k) => consumed.add(k));
  };

  let travelers: TravelerRow[] = [];
  if (meta.traveler_info) {
    travelers = parseTravelerInfo(meta.traveler_info);
    consumed.add('traveler_info');
  }
  if (!travelers.length && meta._ngk_guests_json) {
    travelers = parseNgkGuests(meta._ngk_guests_json);
    consumed.add('_ngk_guests_json');
  }

  const thumbnailUrl =
    meta._ngk_hotel_thumbnail && meta._ngk_hotel_thumbnail.startsWith('http')
      ? meta._ngk_hotel_thumbnail
      : undefined;
  if (thumbnailUrl) consumed.add('_ngk_hotel_thumbnail');

  if (type === 'hotel') {
    addSection(
      section('Hotel stay', [
        field('_ngk_hotel_name', meta._ngk_hotel_name),
        field('_ngk_check_in', meta._ngk_check_in),
        field('_ngk_check_out', meta._ngk_check_out),
        field('_ngk_room_type', meta._ngk_room_type),
        field('_ngk_meal_plan', meta._ngk_meal_plan),
      ]),
      ['_ngk_hotel_name', '_ngk_check_in', '_ngk_check_out', '_ngk_room_type', '_ngk_meal_plan']
    );

    const price = meta._ngk_display_price || meta._ngk_supplier_display_price;
    const currency = meta._ngk_currency || meta._ngk_supplier_currency;
    if (price) {
      addSection(
        section('Pricing', [
          {
            label: 'Total',
            value: currency
              ? `${formatMetaValue('price', price)} ${currency}`
              : formatMetaValue('price', price),
          },
        ]),
        ['_ngk_display_price', '_ngk_supplier_display_price', '_ngk_currency', '_ngk_supplier_currency']
      );
    }

    addSection(
      section('Guest contact', [
        field('_ngk_guest_email', meta._ngk_guest_email),
        field('_ngk_guest_phone', meta._ngk_guest_phone),
        field('_ngk_nationality', meta._ngk_nationality),
        field('_ngk_special_requests', meta._ngk_special_requests),
      ]),
      ['_ngk_guest_email', '_ngk_guest_phone', '_ngk_nationality', '_ngk_special_requests']
    );

    addSection(
      section('References', [
        field('_ngk_booking_ref', meta._ngk_booking_ref),
        field('_ngk_iot_booking_ref', meta._ngk_iot_booking_ref),
      ]),
      ['_ngk_booking_ref', '_ngk_iot_booking_ref']
    );

    addSection(
      section('Booking details', [
        field('Tour Title', meta['Tour Title']),
        field('Tour ID', meta['Tour ID']),
        field('Booking Date', meta['Booking Date']),
        field('Adults', meta.Adults),
        field('Children', meta.Children),
        field('Room Details', meta['Room Details']),
      ]),
      ['Tour Title', 'Tour ID', 'Booking Date', 'Adults', 'Children', 'Room Details']
    );

    const pkgFields = parsePackageInfo(meta.packageInfo);
    if (pkgFields.length) {
      sections.push({ title: 'Package', fields: pkgFields });
      consumed.add('packageInfo');
    }
  } else if (type === 'transfer') {
    addSection(
      section('Transfer', [
        field('_tat_vehicle', meta._tat_vehicle),
        field('_tat_package_name', meta._tat_package_name),
        field('_tat_pickup_name', meta._tat_pickup_name || meta._tat_pickup),
        field('_tat_dropoff_name', meta._tat_dropoff_name || meta._tat_dropoff),
        field('_tat_adults', meta._tat_adults),
        field('_tat_children', meta._tat_children),
        field('_tat_price', meta._tat_price),
      ]),
      [
        '_tat_vehicle',
        '_tat_package_name',
        '_tat_pickup_name',
        '_tat_pickup',
        '_tat_dropoff_name',
        '_tat_dropoff',
        '_tat_adults',
        '_tat_children',
        '_tat_price',
      ]
    );

    addSection(
      section('Terminal service', [
        field('_tat_terminal_name', meta._tat_terminal_name),
        field('_tat_terminal_service_price', meta._tat_terminal_service_price),
        field('_tat_terminal_total', meta._tat_terminal_total),
      ]),
      ['_tat_terminal_name', '_tat_terminal_service_price', '_tat_terminal_total', '_tat_terminal_id']
    );

    const flightFields = parseTatExtraFields(meta);
    if (flightFields.length) {
      sections.push({ title: 'Flight & trip details', fields: flightFields });
      consumed.add('_tat_extra_fields');
      Object.keys(meta).filter((k) => k.startsWith('tat_fields_')).forEach((k) => consumed.add(k));
    }
  } else if (type === 'tour') {
    addSection(
      section('Tour', [
        field('Tour Title', meta['Tour Title']),
        field('Tour ID', meta['Tour ID']),
        field('Booking Date', meta['Booking Date']),
        field('Tour Price', meta['Tour Price']),
        field('Adults', meta.Adults),
        field('Children', meta.Children),
        field('Guests', meta.Guests),
        field('Room Details', meta['Room Details']),
      ]),
      [
        'Tour Title',
        'Tour ID',
        'Booking Date',
        'Tour Price',
        'Adults',
        'Children',
        'Guests',
        'Room Details',
      ]
    );

    addSection(
      section('Charges', [
        field('Supplement/Charges', meta['Supplement/Charges']),
        field('1 Adult Charges', meta['1 Adult Charges']),
        field('2-3 Adult Charges', meta['2-3 Adult Charges']),
        field('Child Charges', meta['Child Charges']),
        field('Extra Nights', meta['Extra Nights']),
      ]),
      ['Supplement/Charges', '1 Adult Charges', '2-3 Adult Charges', 'Child Charges', 'Extra Nights']
    );

    const pkgFields = parsePackageInfo(meta.packageInfo);
    if (pkgFields.length) {
      sections.push({ title: 'Package', fields: pkgFields });
      consumed.add('packageInfo');
    }

    const peakFields = parsePeakDays(meta.peak_days);
    if (peakFields.length) {
      sections.push({ title: 'Peak pricing', fields: peakFields });
      consumed.add('peak_days');
    }
  }

  // Package fields for hotel/tour already handled; generic packageInfo
  if (!consumed.has('packageInfo') && meta.packageInfo) {
    const pkgFields = parsePackageInfo(meta.packageInfo);
    if (pkgFields.length) {
      sections.push({ title: 'Package', fields: pkgFields });
      consumed.add('packageInfo');
    }
  }

  // Remaining visible fields (short values only)
  const otherFields: MetaField[] = [];
  Object.entries(meta).forEach(([key, value]) => {
    if (consumed.has(key) || HIDDEN_KEYS.has(key)) return;
    if (key.startsWith('tat_fields_') && consumed.has('_tat_extra_fields')) return;
    if (isEmpty(value)) return;
    if (value.length > 200) return;
    const f = field(key, value);
    if (f) otherFields.push(f);
  });

  if (otherFields.length) {
    sections.push({ title: 'Additional details', fields: otherFields });
  }

  return { type, sections, travelers, thumbnailUrl };
}
